<script setup>
/**
 * Stands in for a page whose code could not be downloaded, after the self-healing reload has
 * already been tried (lib/chunkRecovery.js). Without it the tab was simply blank.
 *
 * If the failure came from moving between pages, the page you were on is still mounted
 * underneath, so Back just closes this. On a first load there is nothing underneath, so Back
 * goes to the previous address, or home.
 */
import { useRouter } from 'vue-router'
import { RotateCcw, ArrowLeft } from 'lucide-vue-next'
import { BTN_PRIMARY, BTN_SECONDARY } from '../constants/controlStandards'
import { pageLoadFailure } from '../lib/chunkRecovery'

const router = useRouter()

function retry() {
    window.location.assign(pageLoadFailure.value?.path || window.location.href)
}

function back() {
    if (router.currentRoute.value.matched.length) {
        pageLoadFailure.value = null
        return
    }
    if (window.history.length > 1) window.history.back()
    else window.location.assign('/')
}
</script>

<template>
    <div v-if="pageLoadFailure" class="fixed inset-0 z-[100] flex items-start justify-center bg-slate-50/90 px-4 pt-[20vh]">
        <div role="alert" class="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-lg">
            <h2 class="text-base font-semibold text-slate-900">This page didn't load</h2>
            <p class="mt-2 text-sm text-slate-600">
                Part of Cortex couldn't be downloaded. This usually clears on a second try, especially
                just after an update. Nothing you saved has been lost.
            </p>
            <div class="mt-5 flex flex-wrap gap-2">
                <button type="button" :class="BTN_PRIMARY" @click="retry"><RotateCcw class="w-3.5 h-3.5" />Try again</button>
                <button type="button" :class="BTN_SECONDARY" @click="back"><ArrowLeft class="w-3.5 h-3.5" />Back</button>
            </div>
        </div>
    </div>
</template>
